import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { msg } from '@gonfalon/messages/msg';

import { InlineActionToggle } from '../../internal/CreateMetricModal/components/InlineActionToggle';
import { RemovableSettingSection } from '../../internal/CreateMetricModal/components/RemovableSettingSection';
import type { MetricForm } from '../formUtils';
import { MeasurementWindowFields, useMeasurementWindow } from '../MetricWindowingParams/MetricWindowingParams';
import {
  useWinsorization,
  useWinsorizationEnabled,
  WinsorizationBoundFields,
} from '../WinsorizationFields/WinsorizationFields';

import styles from './WinsorizationAndWindowingSections.module.css';

interface WinsorizationAndWindowingSectionsProps {
  context: UseFormReturn<MetricForm>;
  /** The measurement window is warehouse-native and flagged, so the modal decides. */
  isMeasurementWindowAvailable: boolean;
}

/**
 * The measurement window and winsorization as the sectioned layout shows them: a link for
 * whichever has yet to be added, then a card holding those that have been.
 */
export const WinsorizationAndWindowingSections: React.FC<WinsorizationAndWindowingSectionsProps> = ({
  context,
  isMeasurementWindowAvailable,
}) => {
  const measurementWindow = useMeasurementWindow(context);
  const winsorization = useWinsorization(context);
  const dataSourceKey = useWatch({ control: context.control, name: 'dataSourceKey' });
  const isWinsorizationAvailableForSource = useWinsorizationEnabled(dataSourceKey);
  // Reserving the links row per source, not per aggregation, keeps the layout still as the
  // aggregation changes without leaving an empty row on sources that offer neither.
  const isWinsorizationOrWindowingAvailableForSource =
    isMeasurementWindowAvailable || isWinsorizationAvailableForSource;

  return (
    <div className={styles.winsorizationAndWindowing}>
      {isWinsorizationOrWindowingAvailableForSource && (
        <div className={styles.settingLinks}>
          {isMeasurementWindowAvailable && !measurementWindow.isEnabled && (
            <InlineActionToggle
              isExpanded={false}
              onToggle={measurementWindow.setEnabled}
              addLabel={msg('metrics.measurementWindow.addLabel')}
              removeLabel={msg('metrics.measurementWindow.removeLabel')}
            />
          )}
          {winsorization.isAvailable && !winsorization.isEnabled && (
            <InlineActionToggle
              isExpanded={false}
              onToggle={winsorization.setEnabled}
              addLabel={msg('metrics.winsorization.enableLabel')}
              removeLabel={msg('metrics.winsorization.removeLabel')}
            />
          )}
        </div>
      )}
      {/* Empty until something is added; `:empty` hides the card's border. */}
      <div className={styles.settingsCard}>
        {isMeasurementWindowAvailable && measurementWindow.isEnabled && (
          <RemovableSettingSection
            title={msg('metrics.measurementWindow.title')}
            removeLabel={msg('metrics.measurementWindow.removeLabel')}
            onRemove={() => measurementWindow.setEnabled(false)}
          >
            <MeasurementWindowFields context={context} variant="section" />
          </RemovableSettingSection>
        )}
        {winsorization.isAvailable && winsorization.isEnabled && (
          <RemovableSettingSection
            title={msg('metrics.winsorization.title')}
            removeLabel={msg('metrics.winsorization.removeLabel')}
            onRemove={() => winsorization.setEnabled(false)}
          >
            <WinsorizationBoundFields
              context={context}
              lowerPercentile={winsorization.lowerPercentile}
              upperPercentile={winsorization.upperPercentile}
            />
          </RemovableSettingSection>
        )}
      </div>
    </div>
  );
};
